const hasTimeZone = (value) => /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);

export const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const parseBackendDate = (rawValue) => {
  if (!rawValue) return null;

  const stringValue = typeof rawValue === "string" ? rawValue.trim() : String(rawValue);
  if (!stringValue) return null;

  let normalized = stringValue.replace(" ", "T");
  if (!normalized.includes("T")) {
    normalized = `${normalized}T00:00:00`;
  }

  const isoCandidate = hasTimeZone(normalized) ? normalized : `${normalized}Z`;
  const date = new Date(isoCandidate);

  return Number.isNaN(date.getTime()) ? null : date;
};

const dateTimeFormatter = (timeZone, locale = "tr-TR") =>
  new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  });

const FALLBACK_OFFSETS = {
  "Europe/London": 60, // UK BST UTC+1 (fallback; Intl handles DST automatically)
};

const formatWithKnownOffset = (date, offsetMinutes, locale = "en-GB") => {
  const adjusted = new Date(date.getTime() + offsetMinutes * 60000);

  const day = adjusted.getUTCDate().toString().padStart(2, "0");
  const month = (adjusted.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = adjusted.getUTCFullYear();
  const hours = adjusted.getUTCHours().toString().padStart(2, "0");
  const minutes = adjusted.getUTCMinutes().toString().padStart(2, "0");

  return locale === "en-US"
    ? `${month}/${day}/${year} ${hours}:${minutes}`
    : `${day}.${month}.${year} ${hours}:${minutes}`;
};

const formatWithLocalOffset = (date, locale = "en-GB") => {
  const adjusted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  const day = adjusted.getUTCDate().toString().padStart(2, "0");
  const month = (adjusted.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = adjusted.getUTCFullYear();
  const hours = adjusted.getUTCHours().toString().padStart(2, "0");
  const minutes = adjusted.getUTCMinutes().toString().padStart(2, "0");

  return locale === "en-US"
    ? `${month}/${day}/${year} ${hours}:${minutes}`
    : `${day}.${month}.${year} ${hours}:${minutes}`;
};

const extractPartsFromFormatted = (formatted) => {
  if (!formatted) return null;
  const match = formatted.match(/(?<day>\d{2})[./-](?<month>\d{2})[./-](?<year>\d{4})\s+(?<hour>\d{2}):(?<minute>\d{2})/);
  return match ? match.groups : null;
};

export const formatDateTimeForZone = (date, timeZone = "Europe/London", locale = "en-GB") => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const formatter = dateTimeFormatter(timeZone, locale);
  const formatted = formatter.format(date);

  const resolvedZone = formatter.resolvedOptions().timeZone;
  if (resolvedZone !== timeZone) {
    if (FALLBACK_OFFSETS[timeZone] != null) {
      return formatWithKnownOffset(date, FALLBACK_OFFSETS[timeZone], locale);
    }
    return formatWithLocalOffset(date, locale);
  }

  return formatted;
};

export const getDateTimePartsForZone = (date, timeZone = "Europe/London", locale = "en-GB") => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

  const formatter = dateTimeFormatter(timeZone, locale);
  const parts = formatter.formatToParts(date);
  const resolvedZone = formatter.resolvedOptions().timeZone;

  if (resolvedZone !== timeZone) {
    if (FALLBACK_OFFSETS[timeZone] != null) {
      return extractPartsFromFormatted(
        formatWithKnownOffset(date, FALLBACK_OFFSETS[timeZone], locale)
      );
    }
    return extractPartsFromFormatted(formatWithLocalOffset(date, locale));
  }

  return parts.reduce((acc, part) => {
    if (part.type !== "literal") {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});
};

export const formatDateRangeForZone = (
  start,
  end,
  timeZone = "Europe/London",
  locale = "en-GB"
) => {
  const startDate = start instanceof Date ? start : parseBackendDate(start);
  const endDate = end instanceof Date ? end : parseBackendDate(end);

  if (!startDate) return "";

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone,
  });

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  });

  const resolvedDateZone = dateFormatter.resolvedOptions().timeZone;
  const resolvedTimeZone = timeFormatter.resolvedOptions().timeZone;

  const useFallback =
    resolvedDateZone !== timeZone || resolvedTimeZone !== timeZone;

  const formatWith = (formatter, date, offsetMinutes) => {
    if (!date) return "";
    if (offsetMinutes == null) return formatter.format(date);
    const adjusted = new Date(date.getTime() + offsetMinutes * 60000);
    return formatter.format(adjusted);
  };

  const offset = FALLBACK_OFFSETS[timeZone];
  const activeOffset = useFallback ? offset ?? -startDate.getTimezoneOffset() : null;

  const dateStr = formatWith(dateFormatter, startDate, activeOffset);
  const startTimeStr = formatWith(timeFormatter, startDate, activeOffset);
  const endTimeStr = formatWith(timeFormatter, endDate, activeOffset);

  return endTimeStr
    ? `${dateStr} ${startTimeStr} - ${endTimeStr}`
    : `${dateStr} ${startTimeStr}`;
};
